import { concat, race } from 'rxjs'
import { Map, List } from 'immutable'
import moment from 'moment'
import {
  switchMap,
  map,
  flatMap,
  scan,
  withLatestFrom,
  startWith,
  tap,
} from 'rxjs/operators'
import {
  formatDate,
  formatUnixTimeStamp,
} from '@apiEnabler/ob-common/lib/date-utils'
import formatCurrency from '@apiEnabler/ob-common/lib/currency-formatter'
import { cached$, flog } from '@zambezi/caballo-vivo'
import { inc } from 'ramda'

import {
  createClearLoading$,
  createLoading$,
} from '@apiEnabler/api-enabler-common/lib/loading'
import { createErrorWithType } from '@apiEnabler/api-enabler-common/lib/errors'
import {
  createModal$,
  clearModal$,
} from '@apiEnabler/api-enabler-common/lib/dialogs'
import { createStoreNavigateTo$ } from './navigation'
import {
  displayPayYou$,
  displayPayMe$,
  displayPayMeLinkHistory$,
  displayPayYouLinkHistory$,
  displayPayMeReusableLinkHistory$,
  cancelLink$,
  confirmCancelLink$,
  ceaseCancelLink$,
  incrementGenerationPayMe$,
  incrementGenerationPayYou$,
} from './intents'
import { initialData$ } from './customer'
import {
  createPostPayMeList$,
  createPostPayYouList$,
  createPostPayMeReusableList$,
  createGetPayYouLinkDetails$,
  createDeletePayMeReusableCancelLink$,
  createDeletePayMeCancelLink$,
  createPutPayYouCancelLink$,
} from './services'

const friendlyLinkGroupingPayMe = {
  defined: 'Active',
  started: 'In Progress',
  pending: 'In Progress',
  cancelled: 'In Progress',
  inprogress: 'Payment Processing',
  failed: 'Failed',
  successful: 'Completed',
  expired: 'Expired',
  abandoned: 'Cancelled',
}

const friendlyLinkGroupingPayYou = {
  created: 'Active',
  open: 'Active',
  viewed: 'Pending',
  submitted: 'Pending',
  allocated_payment_id: 'Pending',
  sent: 'Successful',
  cancelled: 'Cancelled',
  pending_submission: 'Pending',
  failed: 'Expired',
  expired: 'Expired',
}

const friendlyLinkGroupingReusable = {
  defined: 'Active',
  abandoned: 'Cancelled',
}

function createUpdatePayYouState$() {
  return createPostPayYouList$().pipe(
    map(links =>
      links.map(link => link.get('payoutStatus')).map(homogenisePayYouLink)
    ),
    map(
      links => state =>
        state
          .setIn(['paymeyou', 'payYouLinks', 'links'], links)
          .setIn(
            ['paymeyou', 'payYouLinks', 'linksByFriendlyGroup'],
            links.reduce(byFriendlyGrouping, Map())
          )
    )
  )
}

const youDataGeneration$ = incrementGenerationPayYou$.pipe(
  startWith(0),
  scan(inc, 0),
  flog('Increment you')
)

const cachedCreateUpdatePayYouState$ = cached$(createUpdatePayYouState$)
export const payYouDisplay$ = displayPayYou$.pipe(
  withLatestFrom(youDataGeneration$),
  switchMap(([, dataIndex]) =>
    concat(
      initialData$,
      createLoading$('Loading Pay You'),
      cachedCreateUpdatePayYouState$(dataIndex),
      createStoreNavigateTo$('/paymeyou/pay-you'),
      createClearLoading$('Loading Pay You')
    )
  )
)

function createUpdatePayMeState$() {
  return concat(
    createPostPayMeList$().pipe(
      map(links => links.map(homogenisePayMeLink)),
      map(
        links => state =>
          state
            .setIn(['paymeyou', 'payMeLinks', 'links'], links)
            .setIn(
              ['paymeyou', 'payMeLinks', 'linksByFriendlyGroup'],
              links.reduce(byFriendlyGrouping, Map())
            )
      )
    ),
    createPostPayMeReusableList$().pipe(
      map(links => links.map(homogenisePayMeReusableLink)),
      map(
        links => state =>
          state
            .setIn(['paymeyou', 'payMeReusableLinks', 'links'], links)
            .setIn(
              ['paymeyou', 'payMeReusableLinks', 'linksByFriendlyGroup'],
              links.reduce(byFriendlyGrouping, Map())
            )
      )
    )
  )
}

const meDataGeneration$ = incrementGenerationPayMe$.pipe(
  startWith(0),
  scan(inc, 0),
  flog('Cache index increment')
)

const cachedCreateUpdatePayMeState$ = cached$(createUpdatePayMeState$)
export const payMeDisplay$ = displayPayMe$.pipe(
  withLatestFrom(meDataGeneration$),
  switchMap(([, dataIndex]) =>
    concat(
      initialData$,
      createLoading$('Loading Pay Me'),
      cachedCreateUpdatePayMeState$(dataIndex),
      createStoreNavigateTo$('/paymeyou/pay-me'),
      createClearLoading$('Loading Pay Me')
    )
  )
)

export const payMeReusableLinkHistory$ = displayPayMeReusableLinkHistory$.pipe(
  switchMap(({ linkId }) =>
    concat(
      initialData$,
      createLoading$('Loading Reusable Link History'),
      createGetAndStowPayMeReusableLink$(linkId),
      createStoreNavigateTo$(`/paymeyou/pay-me/reusable-link/${linkId}`),
      createClearLoading$('Loading Reusable Link History')
    )
  )
)

function createGetAndStowPayMeReusableLink$(linkId) {
  return createPostPayMeReusableList$().pipe(
    map(links => checkIfReusableLinkExists$(links, linkId)),
    map(link => homogenisePayMeReusableLink(link)),
    map(link => state => state.setIn(['paymeyou', 'selectedLink'], link))
  )
}

function checkIfReusableLinkExists$(links, linkId) {
  const selectedLink = links.find(
    link => link.get('reusableLinkReference') === linkId
  )

  if (selectedLink) {
    return selectedLink
  }
  throw createErrorWithType('Link not found', 'link-not-found')
}

export const payMeLinkHistory$ = displayPayMeLinkHistory$.pipe(
  switchMap(({ linkId }) =>
    concat(
      initialData$,
      createLoading$(`Loading Pay Me Link History`),
      createPostAndStowPayMeLink$(linkId),
      createStoreNavigateTo$(`/paymeyou/pay-me/${linkId}`),
      createClearLoading$(`Loading Pay Me Link History`)
    )
  )
)

function createPostAndStowPayMeLink$(linkId) {
  return createPostPayMeList$().pipe(
    map(links => checkIfLinkExists$(links, linkId)),
    map(link => homogenisePayMeLink(link)),
    map(link => state => state.setIn(['paymeyou', 'selectedLink'], link))
  )
}

function checkIfLinkExists$(links, linkId) {
  const selectedLink = links.find(link => link.get('plgReference') === linkId)
  if (selectedLink) {
    return selectedLink
  }
  throw createErrorWithType('Link not found', 'link-not-found')
}

export const payYouLinkHistory$ = displayPayYouLinkHistory$.pipe(
  switchMap(({ linkId }) =>
    concat(
      initialData$,
      createLoading$('Loading PayYou Link History'),
      createGetAndStowPayYouLinkDetails$(linkId),
      createStoreNavigateTo$(`/paymeyou/pay-you/${linkId}`),
      createClearLoading$('Loading PayYou Link History')
    )
  )
)

function createGetAndStowPayYouLinkDetails$(linkId) {
  return createGetPayYouLinkDetails$(linkId).pipe(
    map(link => homogenisePayYouLink(link)),
    map(link => state => state.setIn(['paymeyou', 'selectedLink'], link))
  )
}

function homogenisePayMeLink(link) {
  const qrCheck = link.get('linkExpiry') === 'PT5M' ? true : false
  const friendlyName =
    friendlyLinkGroupingPayMe[link.get('plgStatus').toLowerCase()]
  return Map({
    id: link.get('plgReference'),
    type: 'payme',
    label: qrCheck ? 'QR request' : 'Link request',
    payerName: link.get('payerName'),
    payeeName: link.getIn(['payeeAccount', 'name']),
    detailUrl: `/paymeyou/pay-me/${link.get('plgReference')}`,
    dateText: formatLinkDates(link.get('createdDate')),
    amountString: formatCurrency(link.get('currency'), link.get('amount')),
    reference: link.get('fpReference'),
    status: link.get('plgStatus'),
    isCancellable: friendlyName === 'Active',
    friendlyGroup: friendlyName,
    isReusable: false,
    isQR: qrCheck,
    expiryDate: formatLinkDates(calculateExpiryDate(link)),
    companyId: link.get('companyId'),
    brandId: link.get('brandId'),
  })
}

function homogenisePayMeReusableLink(link) {
  const friendlyName =
    friendlyLinkGroupingReusable[link.get('plgStatus').toLowerCase()]
  return Map({
    id: link.get('reusableLinkReference'),
    type: 'payme-reusable',
    label: 'Reusable payment request',
    payeeName: link.getIn(['payeeAccount', 'name']),
    detailUrl: `/paymeyou/pay-me/reusable-link/${link.get(
      'reusableLinkReference'
    )}`,
    dateText: formatLinkDates(link.get('createdDate')),
    amountString: formatCurrency(
      link.get('currency'),
      link.get('defaultAmount')
    ),
    reference: link.get('fpReference'),
    status: link.get('plgStatus'),
    isCancellable: friendlyName === 'Active',
    friendlyGroup: friendlyName,
    isReusable: true,
    isQR: false,
    companyId: link.get('companyId'),
    brandId: link.get('brandId'),
  })
}

function homogenisePayYouLink(link) {
  const friendlyName =
    friendlyLinkGroupingPayYou[link.get('status').toLowerCase()]
  return Map({
    id: link.get('payoutId'),
    type: 'payyou',
    label: 'Link request',
    payerName: link.getIn(['payoutInitiation', 'payerAccount', 'name']),
    payeeName: link.getIn(['payoutInitiation', 'payeeName']),
    detailUrl: `/paymeyou/pay-you/${link.get('payoutId')}`,
    dateText: formatUnixLinkDates(link.get('creationDateTime')),
    amountString: formatCurrency(
      link.getIn(['payoutInitiation', 'payoutAmount', 'currency']),
      link.getIn(['payoutInitiation', 'payoutAmount', 'amount'])
    ),
    reference: link.getIn(['payoutInitiation', 'uniqueIdentifier']),
    status: link.get('status'),
    isCancellable: friendlyName === 'Active',
    friendlyGroup: friendlyName,
    isReusable: false,
    expiryDate: formatUnixLinkDates(
      link.getIn(['payoutInitiation', 'expiryDateTime'])
    ),
  })
}

function formatLinkDates(date) {
  return formatDate(date, 'DD MMM YYYY HH:mm')
}

function formatUnixLinkDates(date) {
  return formatUnixTimeStamp(date, 'DD MMM YYYY HH:mm')
}

function calculateExpiryDate(link) {
  return moment(link.get('createdDate')).add(
    moment.duration(link.get('linkExpiry'))
  )
}

function byFriendlyGrouping(acc, link) {
  const linkFriendlyGroup = link.get('friendlyGroup')
  if (!acc.has(linkFriendlyGroup))
    return acc.set(linkFriendlyGroup, List([link]))
  return acc.update(linkFriendlyGroup, links => links.push(link))
}

function createCancelLinkRequest$(link) {
  const linkId = link.get('id')
  const companyId = link.get('companyId')
  const brandId = link.get('brandId')
  switch (link.get('type')) {
    case 'payme':
      return concat(
        createDeletePayMeCancelLink$(linkId, companyId, brandId),
        createPostAndStowPayMeLink$(linkId)
      )
    case 'payyou':
      return concat(
        createPutPayYouCancelLink$(linkId),
        createGetAndStowPayYouLinkDetails$(linkId)
      )
    case 'payme-reusable':
      return concat(
        createDeletePayMeReusableCancelLink$(linkId, companyId, brandId),
        createGetAndStowPayMeReusableLink$(linkId)
      )
    default:
      return null
  }
}

export const linkCancel$ = cancelLink$.pipe(
  flatMap(link => {
    return concat(
      createModal$('link-cancel.confirm', link).pipe(
        tap(() => incrementGenerationPayMe$.next()),
        tap(() => incrementGenerationPayYou$.next())
      ),
      race(
        concat(
          confirmCancelLink$,
          createModal$('link-cancel.loading'),
          createLoading$('Cancel Loading'),
          clearModal$,
          createCancelLinkRequest$(link),
          createClearLoading$('Cancel Loading')
        ),
        concat(ceaseCancelLink$, clearModal$)
      )
    )
  })
)
